import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { getUserEmail } from '@/utils/storage';
import { ApiEnvelope } from '@/types/package';
import {
  DriverOutstanding,
  GetOutstandingsParams,
  OverrideOutstandingBody,
  ReleaseOutstandingBody,
  SettleOutstandingBody,
} from '@/types/outstanding';

const BASE = '/rent/mgmt/v1/outstandings';

/** Dash Rent nests the payload under the `{ status, message, data }` envelope. */
const unwrap = <T>(payload: ApiEnvelope<T>): T => payload?.data as T;

/** See the same helper in `rentals.ts` — uppercase `ID` PK casing. */
const withId = <T extends { id?: string; ID?: string }>(row: T): T =>
  row && !row.id && row.ID ? { ...row, id: row.ID } : row;

/** Fails fast on a missing or literal "undefined" path param. */
const requireId = (id: string | undefined): string => {
  if (!id || id === 'undefined' || id === 'null') {
    throw new Error('Outstanding id is missing from the URL');
  }
  return id;
};

/**
 * Not paginated — the endpoint returns every matching row (`{ outstandings }`,
 * bare, no `pagination`), same shape as the pre-orders queue. Ops screens over
 * this domain stay small enough that client-side paging over the full result
 * is the right call rather than adding server pagination for it.
 */
export const getOutstandings = async (
  params: GetOutstandingsParams = {}
): Promise<DriverOutstanding[]> => {
  try {
    const response = await Axios.get<
      ApiEnvelope<{ outstandings: DriverOutstanding[] }>
    >(BASE, {
      params: {
        driverID: params.driverID,
        statuses: params.statuses?.join(','),
      },
    });
    const data = unwrap(response.data);
    return (data?.outstandings ?? []).map(withId);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * No `GET /outstandings/:id` exists on the backend today — only the list and
 * the three actions below. The detail page reads the same list and filters
 * client-side rather than adding a dedicated endpoint for one row.
 */
export const getOutstandingById = async (
  id: string
): Promise<DriverOutstanding | undefined> => {
  const rows = await getOutstandings();
  return rows.find((row) => row.id === requireId(id));
};

/** Records a deliberate (partial) repayment. `updatedBy` is stamped here. */
export const settleOutstanding = async (
  id: string,
  body: SettleOutstandingBody
): Promise<DriverOutstanding> => {
  try {
    const response = await Axios.post<ApiEnvelope<DriverOutstanding>>(
      `${BASE}/${requireId(id)}/settle`,
      { ...body, updatedBy: getUserEmail() }
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Write-off — booked on core-payment's books, never a silent delete. `releasedBy` stamped here. */
export const releaseOutstanding = async (
  id: string,
  body: ReleaseOutstandingBody
): Promise<DriverOutstanding> => {
  try {
    const response = await Axios.post<ApiEnvelope<DriverOutstanding>>(
      `${BASE}/${requireId(id)}/release`,
      { ...body, releasedBy: getUserEmail() }
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * One-shot: lets the driver start a new rental while this debt stays open.
 * Consumed automatically the next time that driver's rental is created.
 * `overrideBy` stamped here.
 */
export const overrideOutstanding = async (
  id: string,
  body: OverrideOutstandingBody
): Promise<DriverOutstanding> => {
  try {
    const response = await Axios.post<ApiEnvelope<DriverOutstanding>>(
      `${BASE}/${requireId(id)}/override`,
      { ...body, overrideBy: getUserEmail() }
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
